import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Mail, ShieldCheck, AlertCircle, ArrowRight, Leaf, Cpu, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '@/constants/routes';
import { FloatingLabelInput } from '@/components/forms/FloatingLabelInput';
import { useTheme } from '@/context/ThemeContext';

// Standard RFC 5322 compliant email regex pattern
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const loginSchema = z.object({
  emailOrEmployeeId: z
    .string()
    .min(1, 'Email address is required.')
    .regex(EMAIL_REGEX, 'Please enter a valid email address (e.g. user@enterprise.com)'),
  password: z.string().min(6, 'Password must be at least 6 characters.'),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// ============================================================================
// 🎨 LOGIN HERO IMAGE: Insert your image URL here to test different images
// Works with any web URL (e.g. 'https://images.unsplash.com/...') or local path
// ============================================================================
export const LOGIN_HERO_IMAGE_URL = 'https://i.pinimg.com/736x/6e/46/12/6e4612762f2fc1400e5b26b5ef7a5fba.jpg';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const { resolvedTheme, setThemeMode } = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleToggleTheme = () => {
    setThemeMode(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      emailOrEmployeeId: 'admin@procure.com',
      password: 'Password123!',
      rememberMe: true,
    },
  });

  const emailValue = watch('emailOrEmployeeId');
  const passwordValue = watch('password');

  const onSubmit = async (values: LoginFormValues) => {
    setErrorMessage(null);
    const result = await login(values.emailOrEmployeeId, values.password);
    if (result.success) {
      navigate(ROUTES.DASHBOARD);
    } else {
      setErrorMessage(result.errorMessage || 'Invalid credentials or sign-in information.');
    }
  };

  return (
    <div className="min-h-screen w-full lg:h-screen lg:overflow-hidden grid grid-cols-1 lg:grid-cols-2 bg-background font-heading select-none">
      {/* LEFT COLUMN: Login Form Area */}
      <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-14 xl:p-16 bg-card border-r border-border h-full overflow-y-auto custom-scrollbar">
        {/* Top Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-extrabold shadow-md transform hover:scale-105 transition-transform">
              <Leaf className="h-6.5 w-6.5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-heading text-2xl font-extrabold tracking-tight text-foreground">ProcureAI</span>
              <span className="text-xs font-extrabold tracking-widest uppercase text-primary">Enterprise Core</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleTheme}
            aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
            className="p-2.5 rounded-xl border border-border bg-muted/50 hover:bg-muted text-foreground transition-all hover:border-primary/50 shadow-2xs"
          >
            {resolvedTheme === 'dark' ? (
              <Moon className="h-4.5 w-4.5 text-primary" />
            ) : (
              <Sun className="h-4.5 w-4.5 text-amber-500" />
            )}
          </button>
        </div>

        {/* Center Main Form */}
        <div className="my-auto py-8 space-y-7 max-w-lg w-full mx-auto">
          <div className="space-y-2.5">
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground leading-tight">
              Sign in to ProcureAI
            </h1>
            <p className="text-sm sm:text-base text-foreground-secondary font-semibold leading-relaxed">
              Enter your corporate credentials to access your procurement portal.
            </p>
          </div>

          {/* Error Notification Alert */}
          {errorMessage && (
            <div className="flex items-start gap-3 rounded-2xl border-2 border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-700 dark:text-rose-300 animate-in fade-in">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="flex-1 font-bold">{errorMessage}</div>
            </div>
          )}

          {/* Sign In Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Floating Label Email Input */}
            <FloatingLabelInput
              id="emailOrEmployeeId"
              label="Email Address"
              type="text"
              icon={Mail}
              value={emailValue}
              onChange={(e) => setValue('emailOrEmployeeId', e.target.value, { shouldValidate: true })}
              error={errors.emailOrEmployeeId?.message}
            />

            {/* Floating Label Password Input */}
            <FloatingLabelInput
              id="password"
              label="Password"
              type="password"
              icon={Lock}
              value={passwordValue}
              onChange={(e) => setValue('password', e.target.value, { shouldValidate: true })}
              error={errors.password?.message}
            />

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between text-sm pt-1">
              <label className="flex items-center gap-2.5 font-semibold text-foreground-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={watch('rememberMe')}
                  onChange={(e) => setValue('rememberMe', e.target.checked)}
                  className="rounded border-input text-primary focus:ring-primary h-4.5 w-4.5"
                />
                <span>Remember this device</span>
              </label>

              <button
                type="button"
                onClick={() => navigate(ROUTES.AUTH.FORGOT_PASSWORD)}
                className="font-extrabold text-primary hover:underline focus:outline-none"
              >
                Forgot Password?
              </button>
            </div>

            {/* Main Submit Action Button — Realtime Colors Gradient */}
            <Button
              type="submit"
              size="lg"
              className="w-full h-14 rounded-2xl text-base sm:text-lg font-extrabold shadow-md bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] hover:shadow-lg hover:shadow-indigo-500/30 hover:brightness-110 text-white border-0 transform active:scale-[0.99] transition-all flex items-center justify-center gap-2"
              isLoading={isLoading}
            >
              <span>Sign In to Portal</span>
              <ArrowRight className="h-5 w-5" />
            </Button>
          </form>

          {/* Quick Demo Role Selector */}
          <div className="space-y-2 pt-2 border-t border-border">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-foreground-muted block text-center">
              Quick Role Sign-In (Click to Populate)
            </span>
            <div className="grid grid-cols-3 gap-1.5 text-[11px] font-bold">
              {[
                { label: 'Employee', email: 'employee@procure.com' },
                { label: 'Manager', email: 'manager@procure.com' },
                { label: 'Procurement', email: 'procurement@procure.com' },
                { label: 'Proc. Lead', email: 'procmanager@procure.com' },
                { label: 'Finance', email: 'finance@procure.com' },
                { label: 'Approver', email: 'approver@procure.com' },
                { label: 'Executive', email: 'management@procure.com' },
                { label: 'Auditor', email: 'auditor@procure.com' },
                { label: 'Admin', email: 'admin@procure.com' },
              ].map((r) => (
                <button
                  key={r.email}
                  type="button"
                  onClick={() => {
                    setValue('emailOrEmployeeId', r.email, { shouldValidate: true });
                    setValue('password', 'Password123!', { shouldValidate: true });
                  }}
                  className="py-1 px-1.5 rounded-xl border border-border bg-muted/40 hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-all text-center truncate cursor-pointer text-foreground-secondary"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full h-11 rounded-2xl text-xs sm:text-sm font-bold border border-border hover:bg-muted transition-colors mt-2"
            onClick={() => setErrorMessage('SSO authentication initiated via Microsoft Entra ID.')}
          >
            <Cpu className="h-4 w-4 mr-2 text-primary" />
            <span>Microsoft Entra ID / SAP SSO</span>
          </Button>
        </div>

        {/* Bottom Security Footer */}
        <div className="flex items-center justify-center gap-2 text-xs font-semibold text-foreground-muted border-t border-border/60 pt-4">
          <ShieldCheck className="h-4.5 w-4.5 text-emerald-600 dark:text-emerald-400" />
          <span>Protected by Enterprise TLS 1.3 & SAP BAPI Authorization</span>
        </div>
      </div>

      {/* RIGHT COLUMN: Clean, Unobstructed Hero Image (No animation, no transparency, no pills) */}
      <div className="hidden lg:block w-full h-full relative overflow-hidden bg-[#04050b]">
        <img
          src={LOGIN_HERO_IMAGE_URL}
          alt="ProcureAI Enterprise Platform"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};
